/**
 * Facts the overview states about a plan, all derived from the assignments
 * themselves — no endpoint computes any of this.
 */
import type { Assignment, Participant } from '@/types/assignments'

/**
 * Table numbers arrive as object keys, so they are strings.
 *
 * Exported because this was pasted a third time (SessionCard) and three copies
 * is worse than an imperfect home. A stats module owning a shape helper an
 * edits module also needs is the wrong dependency direction in the abstract,
 * but a neutral fourth module for six lines is the clever abstraction this
 * repo warns about — and every caller already imports from here or from a
 * module that does.
 */
export function tableNumbers(assignment: Assignment): number[] {
  return Object.keys(assignment.tables)
    .map(Number)
    .sort((a, b) => a - b)
}

function people(assignment: Assignment): Participant[] {
  return tableNumbers(assignment).flatMap(n =>
    assignment.tables[n].filter((p): p is Participant => !!p)
  )
}

/**
 * Who each person sat with in one session, as a comparable key. Table numbers
 * carry no meaning to a seating, so a renumbering must compare equal.
 */
function tablematesByName(assignment: Assignment): Map<string, string> {
  const mates = new Map<string, string>()
  tableNumbers(assignment).forEach(n => {
    const names = assignment.tables[n]
      .filter((p): p is Participant => !!p)
      .map(p => p.name)
    names.forEach(name => {
      mates.set(name, names.filter(other => other !== name).sort().join('\x00'))
    })
  })
  return mates
}

/**
 * "6 people", "1 person" — the population of one table, said once.
 *
 * The per-table stats line and the Mark present picker both state this, and
 * strings that agree today drift silently.
 */
export function personCount(count: number): string {
  return `${count} ${count === 1 ? 'person' : 'people'}`
}

/** How many seats a session actually fills — empty chairs are not people. */
export function seatedCount(assignment: Assignment): number {
  return people(assignment).length
}

/** Unordered name pairs sharing a table, counted across the whole program. */
function pairCounts(assignments: Assignment[]): Map<string, number> {
  const counts = new Map<string, number>()
  assignments.forEach(assignment => {
    tableNumbers(assignment).forEach(n => {
      const names = assignment.tables[n]
        .filter((p): p is Participant => !!p)
        .map(p => p.name)
      for (let i = 0; i < names.length; i += 1) {
        for (let j = i + 1; j < names.length; j += 1) {
          const key = [names[i], names[j]].sort().join('\x00')
          counts.set(key, (counts.get(key) ?? 0) + 1)
        }
      }
    })
  })
  return counts
}

/**
 * Of the pairs seated together in one session, how many have met elsewhere in the
 * program. Program-wide repeat counts can stay flat across a shuffle when the
 * change is offset by other sessions; this answers the question a shuffle
 * receipt actually needs to: did the pairs this shuffle just seated together
 * improve?
 */
function sessionRepeatPairs(assignments: Assignment[], sessionNumber: number): number {
  const target = assignments.find(a => a.session === sessionNumber)
  if (!target) return 0

  const counts = pairCounts(assignments)
  let repeats = 0
  tableNumbers(target).forEach(n => {
    const names = target.tables[n].filter((p): p is Participant => !!p).map(p => p.name)
    for (let i = 0; i < names.length; i += 1) {
      for (let j = i + 1; j < names.length; j += 1) {
        const key = [names[i], names[j]].sort().join('\x00')
        if ((counts.get(key) ?? 0) > 1) repeats += 1
      }
    }
  })
  return repeats
}

/**
 * Item 8's three-clause receipt.
 *
 * The new-tablemates count answers the question a user actually has after
 * pressing shuffle: did anything happen? It counts by who sits together, not
 * by table number: a renumbering is not a change. A globally-aware re-solve
 * can legitimately return something close to what was there, and this
 * degrades honestly: "4 of 24 have new tablemates" is the truth.
 *
 * The quality delta is scoped to the shuffled session, not the whole program:
 * a program-wide repeat count can stay flat across a shuffle when the change
 * is offset elsewhere, which reads as "nothing happened" even when the
 * session in question genuinely improved. It's reported in both directions,
 * including when it gets worse — a number that only ever appears as good
 * news is decoration.
 */
export function shuffleReceipt(
  before: Assignment[],
  after: Assignment[],
  sessionNumber: number
): string {
  const beforeSession = before.find(a => a.session === sessionNumber)
  const afterSession = after.find(a => a.session === sessionNumber)
  if (!beforeSession || !afterSession) return `Session ${sessionNumber} shuffled.`

  const hadMates = tablematesByName(beforeSession)
  const hasMates = tablematesByName(afterSession)
  const total = people(afterSession).length
  let changed = 0
  hasMates.forEach((mates, name) => {
    if (hadMates.get(name) !== mates) changed += 1
  })

  // Always "have": tablemate changes come in pairs, so exactly one is impossible.
  const clauses = [
    `${changed} of ${total} ${total === 1 ? 'person' : 'people'} have new tablemates`,
  ]

  const untouched = after
    .map(a => a.session)
    .filter(n => n !== sessionNumber)
    .sort((a, b) => a - b)
  if (untouched.length > 0) {
    clauses.push(`sessions ${untouched.join(', ')} unchanged`)
  }

  const wasSessionRepeats = sessionRepeatPairs(before, sessionNumber)
  const nowSessionRepeats = sessionRepeatPairs(after, sessionNumber)
  clauses.push(
    `${nowSessionRepeats} ${nowSessionRepeats === 1 ? 'pair' : 'pairs'} at session ${sessionNumber}'s tables have already met elsewhere (was ${wasSessionRepeats})`
  )

  return `Session ${sessionNumber} shuffled. ${clauses.join(' · ')}.`
}

/** Average number of distinct people each participant sits with, to one decimal. */
export function uniqueTablematesAverage(assignments: Assignment[]): number {
  const met = new Map<string, Set<string>>()
  assignments.forEach(assignment => {
    tableNumbers(assignment).forEach(n => {
      const names = assignment.tables[n]
        .filter((p): p is Participant => !!p)
        .map(p => p.name)
      names.forEach(name => {
        const seen = met.get(name) ?? new Set<string>()
        names.forEach(other => {
          if (other !== name) seen.add(other)
        })
        met.set(name, seen)
      })
    })
  })

  if (met.size === 0) return 0
  let total = 0
  met.forEach(seen => {
    total += seen.size
  })
  return Math.round((total / met.size) * 10) / 10
}

/**
 * Item 11's person-tracking line, shown above the sessions when a person is
 * selected. Framed repeats-first, coverage-second: repeats is the number that
 * makes a coordinator reach for shuffle, so it leads. This is the one place the
 * product speaks about quality rather than contradictions — see the redesign
 * notes, "Person tracking".
 *
 * "Kathy Veit · meets 5 people more than once, one of them 3 times · sits with
 * 19 of the other 23 participants"
 */
export function personTrackingSummary(name: string, assignments: Assignment[]): string {
  const everyone = new Set<string>()
  assignments.forEach(a => {
    tableNumbers(a).forEach(n => {
      a.tables[n].forEach(person => {
        if (person && person.name !== name) everyone.add(person.name)
      })
    })
  })

  const meetings: number[] = []
  let distinctMet = 0
  pairCounts(assignments).forEach((count, key) => {
    const [a, b] = key.split('\x00')
    if (a !== name && b !== name) return
    distinctMet += 1
    meetings.push(count)
  })

  const repeats = meetings.filter(c => c > 1)
  const maxMeetings = meetings.length > 0 ? Math.max(...meetings) : 0
  const atMax = meetings.filter(c => c === maxMeetings).length

  const totalOthers = everyone.size
  const coverage = `sits with ${distinctMet} of the other ${totalOthers} ${
    totalOthers === 1 ? 'participant' : 'participants'
  }`

  if (repeats.length === 0) {
    return `${name} · never sits with the same person twice · ${coverage}`
  }

  let repeatClause = `meets ${repeats.length} ${
    repeats.length === 1 ? 'person' : 'people'
  } more than once`
  if (maxMeetings >= 3) {
    const word = atMax === 1 ? 'one' : atMax === 2 ? 'two' : String(atMax)
    repeatClause += `, ${word} of them ${maxMeetings} times`
  }

  return `${name} · ${repeatClause} · ${coverage}`
}

/** Distinct linked partnerships in the roster, counted once each. */
export function linkedPairCount(assignments: Assignment[]): number {
  const pairs = new Set<string>()
  assignments.forEach(assignment => {
    people(assignment).forEach(p => {
      if (p.partner) pairs.add([p.name, p.partner].sort().join(' '))
    })
  })
  return pairs.size
}

/**
 * A mid-program rebuild keeps completed sessions and re-seats the rest. The
 * receipt names both halves so a coordinator can see nothing already run was
 * disturbed.
 */
export function rebuildReceipt(k: number, total: number): string {
  if (k <= 0) return 'Sessions rebuilt.'
  const resolvedFrom = k + 1
  const unchangedPhrase =
    k === 1 ? 'Session 1 unchanged' : `Sessions 1\u2013${k} unchanged`
  const newPhrase =
    total - k === 1
      ? `session ${resolvedFrom} has new seating`
      : `sessions ${resolvedFrom}\u2013${total} have new seating`
  return `Sessions rebuilt. ${unchangedPhrase}; ${newPhrase}.`
}
